import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { intelliTrackClient } from '../lib/supabase'
import { UserProfile } from '../types/database'
import Layout from '../components/Layout'
import { ModelInfoCacheService } from '../services/intellijetnet/model-info-cache.service'

interface CreateUserForm {
  email: string
  password: string
  display_name: string
  account_type: 'client' | 'employee' | 'admin' | 'global_admin'
}

export default function Users() {
  const navigate = useNavigate()
  const [users, setUsers] = useState<UserProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [createForm, setCreateForm] = useState<CreateUserForm>({
    email: '',
    password: '',
    display_name: '',
    account_type: 'client'
  })
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [modelNames, setModelNames] = useState<Map<string, string[]>>(new Map())
  const [userAssignments, setUserAssignments] = useState<Map<string, number[]>>(new Map())

  const accountTypes = ['client', 'employee', 'admin', 'global_admin'] as const

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    fetchModelNames()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, userAssignments])

  const fetchUsers = async () => {
    try {
      setLoading(true)
      const { data, error } = await intelliTrackClient
        .from('profiles')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      
      const userData = data || []
      setUsers(userData)
      
      // Fetch market assignments for all users
      const { data: assignments, error: assignmentsError } = await intelliTrackClient
        .from('market_assignments')
        .select('user_id, model_id')
      
      if (!assignmentsError && assignments) {
        // Group assignments by user
        const assignmentMap = new Map<string, number[]>()
        assignments.forEach(assignment => {
          const existing = assignmentMap.get(assignment.user_id) || []
          existing.push(assignment.model_id)
          assignmentMap.set(assignment.user_id, existing)
        })
        setUserAssignments(assignmentMap)
      }
    } catch (err) {
      setError('Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }

  const fetchModelNames = async () => {
    const newModelNames = new Map<string, string[]>()
    
    for (const user of users) {
      const assignments = userAssignments.get(user.id) || []
      if (assignments.length > 0) {
        const names = await ModelInfoCacheService.getDisplayNames(assignments)
        newModelNames.set(user.id, names)
      }
    }
    
    setModelNames(newModelNames)
  }

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    try {
      // Create user in Supabase Auth
      const { data: authData, error: authError } = await intelliTrackClient.auth.admin.createUser({
        email: createForm.email,
        password: createForm.password,
        email_confirm: true,
        user_metadata: {
          display_name: createForm.display_name,
          account_type: createForm.account_type
        }
      })

      if (authError) throw authError

      // Update the profile with account_type and display_name
      if (authData.user) {
        const { error: profileError } = await intelliTrackClient
          .from('profiles')
          .update({
            display_name: createForm.display_name,
            account_type: createForm.account_type
          })
          .eq('id', authData.user.id)

        if (profileError) throw profileError
      }

      setSuccess('User created successfully')
      setShowCreateForm(false)
      setCreateForm({
        email: '',
        password: '',
        display_name: '',
        account_type: 'client'
      })
      fetchUsers()
    } catch (err: any) {
      setError(err.message || 'Failed to create user')
    }
  }

  const handleUpdateAccountType = async (userId: string, newAccountType: string) => {
    try {
      setError(null)
      const { error } = await intelliTrackClient
        .from('profiles')
        .update({ account_type: newAccountType })
        .eq('id', userId)

      if (error) throw error
      
      setSuccess('Account type updated successfully')
      fetchUsers()
    } catch (err: any) {
      setError(err.message || 'Failed to update account type')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading users...</div>
      </div>
    )
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-purple-950">User Management</h1>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="bg-primary text-white hover:bg-purple-900 px-4 py-2 rounded-md text-sm font-medium"
            >
              {showCreateForm ? 'Cancel' : 'Add New User'}
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          {success && (
            <div className="mb-4 rounded-md bg-green-50 p-4">
              <div className="text-sm text-green-700">{success}</div>
            </div>
          )}

          {showCreateForm && (
            <div className="mb-6 bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold mb-4">Create New User</h2>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    required
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Password</label>
                  <input
                    type="password"
                    required
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Display Name</label>
                  <input
                    type="text"
                    required
                    value={createForm.display_name}
                    onChange={(e) => setCreateForm({ ...createForm, display_name: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Account Type</label>
                  <div className="relative mt-1">
                    <select
                      value={createForm.account_type}
                      onChange={(e) => setCreateForm({ ...createForm, account_type: e.target.value as any })}
                      className="appearance-none block w-full bg-white border border-gray-300 rounded-lg py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out"
                    >
                      {accountTypes.map(type => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                        <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <button
                  type="submit"
                  className="bg-primary text-white hover:bg-purple-900 px-4 py-2 rounded-md text-sm font-medium"
                >
                  Create User
                </button>
              </form>
            </div>
          )}

          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {users.map((user) => (
                <li key={user.id} className="px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {user.display_name || 'No display name'}
                      </p>
                      <p className="text-sm text-gray-500">ID: {user.id}</p>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="relative">
                        <select
                          value={user.account_type || 'client'}
                          onChange={(e) => handleUpdateAccountType(user.id, e.target.value)}
                          className="appearance-none bg-white border border-gray-300 rounded-lg py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition duration-150 ease-in-out"
                        >
                          {accountTypes.map(type => (
                            <option key={type} value={type}>
                              {type.charAt(0).toUpperCase() + type.slice(1).replace('_', ' ')}
                            </option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                          <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                          </svg>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/users/${user.id}/assign-markets`)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-sm text-white bg-primary hover:bg-purple-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-150 ease-in-out"
                      >
                        <svg className="mr-2 -ml-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                        </svg>
                        Market Assignments
                      </button>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-start">
                      <span className="text-sm font-medium text-gray-700 mr-2">Markets:</span>
                      {(userAssignments.get(user.id) || []).length > 0 ? (
                        <div className="flex-1">
                          {modelNames.get(user.id) ? (
                            <div className="flex flex-wrap gap-2">
                              {modelNames.get(user.id)!.map((name, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                                >
                                  {name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500 italic">Loading...</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 italic">None assigned</span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </Layout>
  )
}